import { UNITS } from "../../lib/slaLadder";
import { MAX_REMINDERS } from "../../lib/slaQuestion";
import { Select, inputClass } from "./primitives";

function Branch({ tone, title, children, testId }) {
  const tones = {
    yes: { dot: "#12B76A", text: "#067647", bg: "#ECFDF3", line: "#ABEFC6" },
    no: { dot: "#F04438", text: "#B42318", bg: "#FEF3F2", line: "#FECDCA" },
    silent: { dot: "#F79009", text: "#B54708", bg: "#FFFAEB", line: "#FEDF89" },
  }[tone];
  return (
    <div
      data-testid={testId}
      className="min-w-0 rounded-lg border px-3 py-3"
      style={{ background: tones.bg, borderColor: tones.line }}
    >
      <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold" style={{ color: tones.text }}>
        <span className="h-2 w-2 rounded-full" style={{ background: tones.dot }} />
        {title}
      </div>
      <div className="flex flex-col gap-2 text-[13px] text-[#374151]">{children}</div>
    </div>
  );
}

function UnitSelect({ value, onChange, label, testId }) {
  return (
    <Select className="w-[94px]" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testId}>
      {UNITS.map((u) => (
        <option key={u.value} value={u.value}>
          {u.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * The condition after a question step: what happens on each of the customer's three
 * possible outcomes. Drawn in the ladder between the question and the step that waits on it.
 *
 *   q          the question's settings as the editor holds them (see lib/slaQuestion.js)
 *   onChange   called with the fields that changed
 *   next       the staff step a "no" releases, with its position, or null when none follows
 */
export default function SlaConditionNode({ id, q, onChange, next }) {
  const nextLabel = next ? `step ${next.position} (${next.name})` : null;
  return (
    <div className="flex gap-4" data-testid={`condition-${id}`}>
      <div className="flex flex-col items-center">
        <div
          className="mt-[6px] h-[18px] w-[18px] flex-none rotate-45 rounded-[3px] border-2 border-[#2667B5] bg-white"
          aria-hidden="true"
        />
        <div className="my-1 w-[2px] flex-1 rounded-full bg-[#8FB8E6]" />
      </div>
      <div className="mb-4 min-w-0 flex-1 rounded-xl border border-[#BBD4F1] bg-[#F5F9FE] px-4 py-3">
        <div className="text-[13px] font-semibold text-[#1F5596]">Condition: has the customer's issue been resolved?</div>
        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-3">
          <Branch tone="yes" title="Yes" testId={`branch-yes-${id}`}>
            <span>Stop the ladder. Nothing more is sent.</span>
          </Branch>

          <Branch tone="no" title="No" testId={`branch-no-${id}`}>
            {nextLabel ? <span>Send {nextLabel}.</span> : <span>No step follows, so the ladder closes.</span>}
            <div className="flex flex-wrap items-center gap-2">
              <Select
                className="w-[150px]"
                value={q.noNow ? "now" : "after"}
                onChange={(e) => onChange({ noNow: e.target.value === "now" })}
                aria-label="When to send after a no"
                data-testid={`no-mode-${id}`}
              >
                <option value="now">Immediately</option>
                <option value="after">After a wait</option>
              </Select>
              {!q.noNow && (
                <>
                  <input
                    type="number"
                    min="0"
                    className={`${inputClass} !w-[76px] text-right`}
                    value={q.noAmount}
                    onChange={(e) => onChange({ noAmount: e.target.value })}
                    aria-label="Wait after a no"
                    data-testid={`no-amount-${id}`}
                  />
                  <UnitSelect value={q.noUnit} onChange={(noUnit) => onChange({ noUnit })} label="Wait unit after a no" testId={`no-unit-${id}`} />
                </>
              )}
            </div>
          </Branch>

          <Branch tone="silent" title="No answer" testId={`branch-silent-${id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span>Wait</span>
              <input
                type="number"
                min="1"
                className={`${inputClass} !w-[76px] text-right`}
                value={q.waitAmount}
                onChange={(e) => onChange({ waitAmount: e.target.value })}
                aria-label="Wait for an answer"
                data-testid={`wait-amount-${id}`}
              />
              <UnitSelect value={q.waitUnit} onChange={(waitUnit) => onChange({ waitUnit })} label="Wait unit for an answer" testId={`wait-unit-${id}`} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span>then remind, up to</span>
              <input
                type="number"
                min="0"
                max={MAX_REMINDERS}
                className={`${inputClass} !w-[64px] text-right`}
                value={q.reminders}
                onChange={(e) => onChange({ reminders: e.target.value })}
                aria-label="Number of reminders"
                data-testid={`reminders-${id}`}
              />
              <span>{Number(q.reminders) === 1 ? "time" : "times"}</span>
            </div>
            <input
              className={inputClass}
              value={q.reminderTemplate}
              placeholder="Reminder template (blank: ask again)"
              onChange={(e) => onChange({ reminderTemplate: e.target.value })}
              aria-label="Reminder template"
              data-testid={`reminder-template-${id}`}
            />
            <span className="text-[12px] text-[#6B7280]">
              Still no answer after the last reminder: the complaint and the ladder are closed. Nobody is escalated.
            </span>
          </Branch>
        </div>
      </div>
    </div>
  );
}
